'use client'

import type { ColumnTrack } from '@/components/blocks/app-layout'

import {
    AppShell,
    BoardLayout,
    DashboardLayout,
    LayoutColumns,
    LayoutContainer,
    PageBody,
    WorkspaceLayout
} from '@/components/blocks/app-layout'

/*
 * GoodUnited / Page layout templates. Figma: 30 / Templates / Page layouts.
 * Ten starters, each built from the shared AppShell. Containers are empty Content slots:
 * place real components inside them, never change the shell, spacing or corners.
 */
export type PageLayoutKind =
    | 'one-column'
    | 'two-columns'
    | 'three-columns'
    | 'four-columns'
    | 'main-aside'
    | 'aside-main'
    | 'dashboard'
    | 'workspace'
    | 'board'
    | 'dashboard-dark'

export const PAGE_LAYOUTS: { kind: PageLayoutKind, title: string, description: string }[] = [
    { kind: 'one-column', title: 'One column', description: 'A single full-width container for forms and long reads.' },
    { kind: 'two-columns', title: 'Two columns', description: 'Two equal containers side by side.' },
    { kind: 'three-columns', title: 'Three columns', description: 'Three equal containers for summaries.' },
    { kind: 'four-columns', title: 'Four columns', description: 'Four equal containers for metrics.' },
    { kind: 'main-aside', title: 'Main with aside', description: 'A wide main container with a narrow aside on the right.' },
    { kind: 'aside-main', title: 'Aside with main', description: 'A narrow aside on the left with a wide main container.' },
    { kind: 'dashboard', title: 'Dashboard', description: 'Four metrics, a full-width section and three cards.' },
    { kind: 'workspace', title: 'Workspace', description: 'List, detail and context panels that fill the page edge to edge.' },
    { kind: 'board', title: 'Board', description: 'Four lanes for kanban-style work.' },
    { kind: 'dashboard-dark', title: 'Dashboard, dark mode', description: 'The dashboard on the dark semantic set.' }
]

const columnTracks: Partial<Record<PageLayoutKind, { track: ColumnTrack, count: number }>> = {
    'one-column': { track: '1', count: 1 },
    'two-columns': { track: '2', count: 2 },
    'three-columns': { track: '3', count: 3 },
    'four-columns': { track: '4', count: 4 },
    'main-aside': { track: '2:1', count: 2 },
    'aside-main': { track: '1:2', count: 2 }
}

/* Template/PageLayout: AppShell with the chosen body. The dark version is pinned to dark by its view. */
export function PageLayoutTemplate({ kind }: { kind: PageLayoutKind }) {
    if (kind === 'workspace') {
        return (
            <AppShell title='Page title'>
                <WorkspaceLayout className='min-h-full' />
            </AppShell>
        )
    }

    const columns = columnTracks[kind]

    return (
        <AppShell title='Page title'>
            <PageBody title='Page title'>
                {columns && (
                    <LayoutColumns track={columns.track} className='flex-1'>
                        {Array.from({ length: columns.count }, (_, index) => <LayoutContainer key={index} className='min-h-120' />)}
                    </LayoutColumns>
                )}
                {(kind === 'dashboard' || kind === 'dashboard-dark') && <DashboardLayout />}
                {kind === 'board' && <BoardLayout />}
            </PageBody>
        </AppShell>
    )
}
